import PropTypes from 'prop-types';
import React from 'react';
import Dialog from './Dialog';
import { translate } from '../../../common/i18n';
import {
    loginAutotraderSite,
    registerAutotraderSite,
    clearAutotraderSession,
    isAutotraderSiteLoggedIn,
    getStoredAutotraderSiteUser,
} from '../../../common/autotrader-session';

const Form = ({ closeDialog, onSessionChange, registerRefresh }) => {
    const [, setTick] = React.useState(0);
    const refresh = React.useCallback(() => setTick(t => t + 1), []);
    React.useEffect(() => {
        if (typeof registerRefresh === 'function') {
            registerRefresh(refresh);
        }
    }, [registerRefresh, refresh]);

    const loggedIn = isAutotraderSiteLoggedIn();
    const user = getStoredAutotraderSiteUser();
    const [email, setEmail] = React.useState((user && user.email) || '');
    const [password, setPassword] = React.useState('');
    const [confirmPassword, setConfirmPassword] = React.useState('');
    const [error, setError] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [isRegisterMode, setIsRegisterMode] = React.useState(false);

    const onSubmit = async e => {
        e.preventDefault();
        setError('');
        
        if (isRegisterMode) {
            if (!email || !password || !confirmPassword) {
                setError(String(translate('Please enter email, password and confirm password.')));
                return;
            }
            if (password !== confirmPassword) {
                setError(String(translate('Passwords do not match.')));
                return;
            }
            setBusy(true);
            try {
                await registerAutotraderSite(email.trim(), password);
                setPassword('');
                setConfirmPassword('');
                if (onSessionChange) {
                    onSessionChange();
                }
                refresh();
                closeDialog();
            } catch (err) {
                setError(err.message || String(translate('Registration failed.')));
            } finally {
                setBusy(false);
            }
        } else {
            if (!email || !password) {
                setError(String(translate('Please enter email and password.')));
                return;
            }
            setBusy(true);
            try {
                await loginAutotraderSite(email.trim(), password);
                setPassword('');
                if (onSessionChange) {
                    onSessionChange();
                }
                refresh();
                closeDialog();
            } catch (err) {
                setError(err.message || String(translate('Login failed.')));
            } finally {
                setBusy(false);
            }
        }
    };

    const signOut = async () => {
        await clearAutotraderSession();
        if (onSessionChange) {
            onSessionChange();
        }
        refresh();
    };

    return (
        <div style={{ padding: '16px', maxWidth: '440px' }}>
            {loggedIn ? (
                <div>
                    <p>
                        {String(translate('Signed in'))}
                        {user && user.email ? `: ${user.email}` : ''}
                    </p>
                    <button type="button" className="button" onClick={signOut}>
                        {String(translate('Sign out of site account'))}
                    </button>
                </div>
            ) : (
                <form onSubmit={onSubmit}>
                    <div style={{ marginBottom: '12px' }}>
                        <label htmlFor="autotrader-site-email">
                            <div>{String(translate('Email'))}</div>
                            <input
                                id="autotrader-site-email"
                                type="email"
                                value={email}
                                onChange={ev => setEmail(ev.target.value)}
                                style={{ width: '100%', marginTop: '4px', boxSizing: 'border-box' }}
                                autoComplete="username"
                                required
                            />
                        </label>
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                        <label htmlFor="autotrader-site-password">
                            <div>{String(translate('Password'))}</div>
                            <input
                                id="autotrader-site-password"
                                type="password"
                                value={password}
                                onChange={ev => setPassword(ev.target.value)}
                                style={{ width: '100%', marginTop: '4px', boxSizing: 'border-box' }}
                                autoComplete="current-password"
                                required
                            />
                        </label>
                    </div>
                    {isRegisterMode ? (
                        <div style={{ marginBottom: '12px' }}>
                            <label htmlFor="autotrader-site-confirm-password">
                                <div>{String(translate('Confirm Password'))}</div>
                                <input
                                    id="autotrader-site-confirm-password"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={ev => setConfirmPassword(ev.target.value)}
                                    style={{ width: '100%', marginTop: '4px', boxSizing: 'border-box' }}
                                    autoComplete="new-password"
                                    required
                                />
                            </label>
                        </div>
                    ) : null}
                    {error ? (
                        <p className="error-msg" style={{ color: '#c62828', marginBottom: '8px' }}>
                            {error}
                        </p>
                    ) : null}
                    
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                        <button type="submit" className="button" disabled={busy}>
                            {String(busy 
                                ? (isRegisterMode ? translate('Registering...') : translate('Signing in...')) 
                                : (isRegisterMode ? translate('Register') : translate('Sign in'))
                            )}
                        </button>
                        
                        <button 
                            type="button" 
                            style={{ 
                                background: 'none', 
                                border: 'none', 
                                color: '#2a3052', 
                                textDecoration: 'underline', 
                                cursor: 'pointer',
                                padding: 0
                            }}
                            onClick={() => {
                                setError('');
                                setIsRegisterMode(!isRegisterMode);
                            }}
                        >
                            {isRegisterMode 
                                ? String(translate('Already have an account? Sign in')) 
                                : String(translate('Don\'t have an account? Register'))
                            }
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

Form.propTypes = {
    closeDialog    : PropTypes.func.isRequired,
    onSessionChange: PropTypes.func,
    registerRefresh: PropTypes.func,
};

export default class AutotraderSiteLoginDialog extends Dialog {
    constructor(options = {}) {
        let refreshChild = () => {};
        const closeDialog = () => {
            this.close();
        };
        const content = (
            <Form
                closeDialog={closeDialog}
                onSessionChange={options.onSessionChange}
                registerRefresh={fn => {
                    refreshChild = fn;
                }}
            />
        );
        super('autotrader-site-login-dialog', String(translate('Account details')), content, {
            width    : 'auto',
            height   : 'auto',
            resizable: false,
        });
        this.refreshForm = () => refreshChild();
        this.registerCloseOnOtherDialog();
    }

    open() {
        this.refreshForm();
        super.open();
    }
}
